import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Checkbox,
  FormControlLabel,
  Chip,
  RadioGroup,
  Radio,
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import WarningIcon from '@mui/icons-material/Warning'
import RefreshIcon from '@mui/icons-material/Refresh'
import AddIcon from '@mui/icons-material/Add'
import { supabase } from '../../config/supabase'
import type { Chain, Undertaker, ServicesTemplateStructure } from '../../types'
import LogoUpload from '../../components/LogoUpload/LogoUpload'
import GoogleRatingInfo from '../../components/GoogleRatingInfo'
import BusinessReviewsList from '../../components/BusinessReviewsList'
import RichTextEditor from '../../components/RichTextEditor/RichTextEditor'
import MapDrawing from '../../components/MapDrawing/MapDrawing'
import ServicesFormRenderer from '../../components/ServicesFormRenderer/ServicesFormRenderer'
import { PrisberegnerForm, PrisberegnerFormHandle } from '../../components/PrisberegnerForm'
import { useAuth } from '../../hooks/useAuth'
import {
  geoJSONToServiceArea,
  serviceAreaToGeoJSON,
} from '../../utils/serviceAreaUtils'
import {
  validateCompanyFields,
  hasValidationErrors,
  normalizeCompanyFields,
  getPhoneError,
  getEmailError,
  getCVRError,
  getWebsiteError,
  getFacebookError,
  getInstagramError,
  getGoogleBusinessError,
  getTrustpilotError,
  type ValidationErrors,
} from '../../utils/validation'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  )
}

// Helper to check if boost_area has actual polygon data (vs being a placeholder {})
function hasValidBoostArea(boostArea: any): boolean {
  return boostArea && 
    boostArea.type === 'polygon' && 
    Array.isArray(boostArea.polygons) && 
    boostArea.polygons.length > 0
}

// Helper to check if boost_area exists (even as placeholder) - for showing UI
function hasBoostAreaEnabled(boostArea: any): boolean {
  return boostArea !== null && boostArea !== undefined && typeof boostArea === 'object'
}

export default function ChainForm() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEdit = Boolean(id)
  const { isAdmin, user } = useAuth()

  const [activeTab, setActiveTab] = useState(0)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [hasAccess, setHasAccess] = useState(false)
  const [checkingAccess, setCheckingAccess] = useState(true)

  const [formData, setFormData] = useState<Partial<Chain>>({
    name: '',
    slug: '',
    phone: '',
    email: '',
    website: '',
    cvr_number: '',
    contact_person_name: '',
    contact_person_email: '',
    facebook_link: '',
    google_business_link: '',
    instagram: '',
    trustpilot_url: '',
    description: '',
    logo_url: '',
    usp1: '',
    usp2: '',
    usp3: '',
    rating: undefined,
    use_chain_service_area: false,
    global_boost: false,
    boost_priority: 0,
    published: true,
    is_verified: false,
  })

  const [locations, setLocations] = useState<Undertaker[]>([])
  const [servicesTemplate, setServicesTemplate] = useState<ServicesTemplateStructure | null>(null)
  const [servicesData, setServicesData] = useState<Record<string, any>>({})
  const [fetchingRating, setFetchingRating] = useState(false)
  const [ratingMessage, setRatingMessage] = useState<string | null>(null)
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({})
  
  // Ref for Prisberegner form to call save from parent
  const prisberegnerFormRef = useRef<PrisberegnerFormHandle>(null)

  // Fetch rating from Trustpilot and save it
  const fetchTrustpilotRating = async () => {
    if (!formData.trustpilot_url) {
      setRatingMessage('❌ Indtast først en Trustpilot URL')
      return
    }

    setFetchingRating(true)
    setRatingMessage(null)

    try {
      const { data, error } = await supabase.functions.invoke('fetch-trustpilot-rating', {
        body: { trustpilotUrl: formData.trustpilot_url }
      })

      if (error) throw error

      if (data.error) {
        setRatingMessage(`❌ ${data.error}`)
        return
      }

      if (data.rating !== undefined) {
        // Update local state
        setFormData(prev => ({ ...prev, rating: data.rating, review_count: data.reviewCount ?? null }))
        
        // Auto-save the rating and review count to database
        if (id) {
          const { error: saveError } = await supabase
            .from('chains')
            .update({ 
              rating: data.rating,
              review_count: data.reviewCount ?? null,
              rating_updated_at: new Date().toISOString()
            })
            .eq('id', id)

          if (saveError) {
            console.error('Error saving rating:', saveError)
            setRatingMessage(`⚠️ Rating hentet (${data.rating}) men kunne ikke gemmes: ${saveError.message}`)
            return
          }
        }

        const reviewText = data.reviewCount ? ` (${data.reviewCount} anmeldelser)` : ''
        setRatingMessage(`✅ Rating hentet og gemt: ${data.rating}${reviewText}`)
      }
    } catch (err: any) {
      console.error('Error fetching rating:', err)
      setRatingMessage(`❌ Fejl: ${err.message || 'Kunne ikke hente rating'}`)
    } finally {
      setFetchingRating(false)
    }
  }

  // Fetch chain data only once on mount (when id changes)
  // NOT when user/isAdmin changes - that would overwrite form data!
  useEffect(() => {
    if (isEdit && id) {
      fetchChain()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit])

  // Check user access separately - this can run when user changes
  // but doesn't affect form data
  useEffect(() => {
    if (isEdit && id && user) {
      checkUserAccess()
    } else if (!isEdit) {
      // For new chains, only admins can create
      setCheckingAccess(false)
      setHasAccess(isAdmin)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit, isAdmin, user?.id])

  // Fetch services template on mount
  useEffect(() => {
    const fetchServicesTemplate = async () => {
      try {
        const { data, error } = await supabase
          .from('services_templates')
          .select('*')
          .eq('is_active', true)
          .limit(1)

        if (error) throw error
        if (data && data.length > 0 && data[0].template) {
          setServicesTemplate(data[0].template)
        }
      } catch (err: any) {
        console.error('Error fetching services template:', err)
      }
    }

    fetchServicesTemplate()
  }, [])

  const checkUserAccess = async () => {
    if (!user || !id) {
      setCheckingAccess(false)
      return
    }

    // Admins always have access
    if (isAdmin) {
      setHasAccess(true)
      setCheckingAccess(false)
      return
    }

    try {
      // Check if user has a chain claim for this chain
      const { data, error } = await supabase
        .rpc('user_has_chain_access', {
          p_user_id: user.id,
          p_chain_id: id,
        })

      if (error) {
        console.error('Error checking access:', error)
        setHasAccess(false)
      } else {
        setHasAccess(data === true)
      }
    } catch (err) {
      console.error('Error checking access:', err)
      setHasAccess(false)
    } finally {
      setCheckingAccess(false)
    }
  }

  const fetchChain = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('chains')
        .select('*, locations:undertakers(*)')
        .eq('id', id)
        .single()

      if (error) throw error
      if (data) {
        setFormData(data)
        setLocations(data.locations || [])
        // Load services data
        if (data.services_data?.data) {
          setServicesData(data.services_data.data)
        }
      }
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (field: string) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.value
    setFormData({ ...formData, [field]: value })
    
    // Real-time validation for specific fields
    const validationFn: Record<string, (v: string) => string | null> = {
      phone: getPhoneError,
      email: getEmailError,
      contact_person_email: getEmailError,
      cvr_number: getCVRError,
      website: getWebsiteError,
      facebook_link: getFacebookError,
      instagram: getInstagramError,
      google_business_link: getGoogleBusinessError,
      trustpilot_url: getTrustpilotError,
    }
    
    if (validationFn[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: validationFn[field](value)
      }))
    }
  }

  // Handle services data field changes
  const handleServicesDataChange = (fieldId: string, value: any) => {
    setServicesData(prev => ({
      ...prev,
      [fieldId]: value
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate all fields before saving (use trustpilot_url for chains)
    const dataToValidate = { ...formData, trustpilot: formData.trustpilot_url }
    const errors = validateCompanyFields(dataToValidate)
    // Map trustpilot error to trustpilot_url
    if (errors.trustpilot) {
      errors.trustpilot_url = errors.trustpilot
    }
    setValidationErrors(errors)
    
    if (hasValidationErrors(errors)) {
      setError('Ret venligst fejlene i formularen før du gemmer.')
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    
    setSaving(true)
    setError(null)
    setSuccess(false)

    try {
      // Generate slug if new
      if (!isEdit && !formData.slug) {
        const slug = formData.name
          ?.toLowerCase()
          .replace(/æ/g, 'ae')
          .replace(/ø/g, 'oe')
          .replace(/å/g, 'aa')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '')
        formData.slug = slug
      }

      // Normalize fields before saving
      const normalizedData = normalizeCompanyFields(formData)
      
      // Explicitly define only the fields that exist in the chains table
      // This prevents any populated relations or computed fields from being saved
      const dataToSave: any = {
        name: formData.name,
        slug: formData.slug,
        phone: normalizedData.phone || formData.phone,
        email: formData.email,  // Email doesn't need normalization
        website: formData.website,
        cvr_number: normalizedData.cvr_number || formData.cvr_number,
        contact_person_name: formData.contact_person_name,
        contact_person_email: formData.contact_person_email,  // Email doesn't need normalization
        facebook_link: formData.facebook_link,
        google_business_link: formData.google_business_link,
        instagram: formData.instagram,
        trustpilot_url: formData.trustpilot_url,
        rating: formData.rating,
        rating_updated_at: (formData as any).rating_updated_at,
        description: formData.description,
        logo_url: formData.logo_url,
        usp1: formData.usp1,
        usp2: formData.usp2,
        usp3: formData.usp3,
        use_chain_service_area: formData.use_chain_service_area,
        chain_service_area: formData.chain_service_area,
        global_boost: formData.global_boost,
        boost_priority: formData.boost_priority,
        // Only save boost_area if it has actual polygon data, otherwise null
        boost_area: hasValidBoostArea((formData as any).boost_area) ? (formData as any).boost_area : null,
        published: formData.published,
        is_verified: formData.is_verified || false,
        prisberegner_enabled: formData.prisberegner_enabled || false,
        services_data: {
          last_updated: new Date().toISOString(),
          data: servicesData
        }
      }
      
      // Remove undefined values to avoid overwriting with null
      Object.keys(dataToSave).forEach(key => {
        if (dataToSave[key] === undefined) {
          delete dataToSave[key]
        }
      })

      if (isEdit) {
        const { error } = await supabase
          .from('chains')
          .update(dataToSave)
          .eq('id', id)

        if (error) throw error
      } else {
        const { error } = await supabase
          .from('chains')
          .insert([dataToSave])

        if (error) throw error
      }

      // Save Prisberegner data if enabled
      if (formData.prisberegner_enabled && prisberegnerFormRef.current) {
        const prisberegnerSaved = await prisberegnerFormRef.current.save()
        if (!prisberegnerSaved) {
          setError('Kæde gemt, men kunne ikke gemme prisberegner data. Prøv igen.')
          return
        }
      }

      setSuccess(true)
      // Re-fetch to ensure we have the latest data
      if (isEdit) {
        fetchChain()
      }
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const handleRemoveLocation = async (locationId: string) => {
    if (!window.confirm('Er du sikker på at du vil fjerne denne lokation fra kæden?')) {
      return
    }

    try {
      const { error } = await supabase
        .from('undertakers')
        .update({ chain_id: null })
        .eq('id', locationId)

      if (error) throw error

      setLocations(locations.filter(l => l.id !== locationId))
      setSuccess(true)
      setTimeout(() => setSuccess(false), 2000)
    } catch (err: any) {
      setError(err.message)
    }
  }

  const handleToggleSeparateEntity = async (locationId: string, currentValue: boolean) => {
    try {
      const { error } = await supabase
        .from('undertakers')
        .update({ is_separate_entity: !currentValue })
        .eq('id', locationId)

      if (error) throw error

      setLocations(locations.map(l =>
        l.id === locationId ? { ...l, is_separate_entity: !currentValue } : l
      ))
      setSuccess(true)
      setTimeout(() => setSuccess(false), 2000)
    } catch (err: any) {
      setError(err.message)
    }
  }

  if (loading || checkingAccess) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!hasAccess) {
    return (
      <Alert severity="error">
        Du har ikke adgang til at redigere denne kæde.
      </Alert>
    )
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/undertakers')}
          sx={{ minWidth: 'auto' }}
        >
          Tilbage
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {isEdit ? 'Rediger kæde' : 'Opret ny kæde'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          ✓ Gemt succesfuldt!
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Card>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider', px: 3, pt: 2 }}
          >
            <Tab label="Profil" />
            <Tab label="Lokationer" disabled={!isEdit} />
            <Tab label="Serviceområde" disabled={!isEdit} />
            <Tab label="Services" disabled={!isEdit} />
            {(isAdmin || formData.prisberegner_enabled) && <Tab label="Prisberegner" disabled={!isEdit} />}
            {isAdmin && <Tab label="Global Boost" disabled={!isEdit} />}
          </Tabs>

          <CardContent sx={{ p: 4 }}>
            {/* Tab 1: Profile */}
            <TabPanel value={activeTab} index={0}>
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Kæde Information
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    required
                    label="Kædenavn"
                    value={formData.name || ''}
                    onChange={handleChange('name')}
                    helperText="Dette navn bruges på hjemmesiden"
                  />
                </Box>

                {/* Verification Toggle - Admin only */}
                {isAdmin && (
                  <Box sx={{ mb: 3, p: 2, bgcolor: 'success.50', borderRadius: 2, border: '1px solid', borderColor: 'success.100' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.is_verified || false}
                          onChange={(e) => setFormData({ ...formData, is_verified: e.target.checked })}
                          color="success"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.900' }}>
                            ✓ Kæde er verificeret
                          </Typography>
                          <Typography variant="caption" color="success.800">
                            Marker denne hvis kædens profil er manuelt udfyldt og valideret. Viser et grønt badge på hjemmesiden for alle lokationer i kæden.
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>
                )}

                {isEdit && (
                  <Box sx={{ mb: 2 }}>
                    <TextField
                      fullWidth
                      label="Slug (URL-venligt navn)"
                      value={formData.slug || ''}
                      onChange={handleChange('slug')}
                      helperText="Bruges i URL: /bedemand/[slug] - Kun små bogstaver, tal og bindestreger"
                    />
                  </Box>
                )}

                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 4 }}>
                  Kontakt
                </Typography>
                <Divider sx={{ mb: 3 }} />

                {/* External contact info */}
                <Alert severity="info" sx={{ mb: 2 }}>
                  <strong>🌐 Vises eksternt på hjemmesiden</strong>
                </Alert>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  <Box sx={{ flex: '1 1 300px' }}>
                    <TextField
                      fullWidth
                      label="Telefon"
                      value={formData.phone || ''}
                      onChange={handleChange('phone')}
                      placeholder="12 34 56 78"
                      error={!!validationErrors.phone}
                      helperText={validationErrors.phone || 'F.eks. 12 34 56 78'}
                    />
                  </Box>

                  <Box sx={{ flex: '1 1 300px' }}>
                    <TextField
                      fullWidth
                      type="email"
                      label="Email"
                      value={formData.email || ''}
                      onChange={handleChange('email')}
                      error={!!validationErrors.email}
                      helperText={validationErrors.email || 'Vises på hjemmesiden'}
                    />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  <Box sx={{ flex: '1 1 300px' }}>
                    <TextField
                      fullWidth
                      label="Hjemmeside"
                      value={formData.website || ''}
                      onChange={handleChange('website')}
                      placeholder="https://www.example.dk"
                      error={!!validationErrors.website}
                      helperText={validationErrors.website || 'Vises på hjemmesiden'}
                    />
                  </Box>

                  <Box sx={{ flex: '1 1 300px' }}>
                    <TextField
                      fullWidth
                      label="CVR-nummer"
                      value={formData.cvr_number || ''}
                      onChange={handleChange('cvr_number')}
                      placeholder="12345678"
                      error={!!validationErrors.cvr_number}
                      helperText={validationErrors.cvr_number || '8 cifre'}
                    />
                  </Box>
                </Box>

                {/* Internal contact info */}
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 4 }}>
                  Kontaktperson (intern)
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Alert severity="warning" sx={{ mb: 2 }}>
                  <strong>🔒 Kun til internt brug - vises IKKE eksternt</strong>
                </Alert>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  <Box sx={{ flex: '1 1 300px' }}>
                    <TextField
                      fullWidth
                      label="Kontaktperson navn"
                      value={formData.contact_person_name || ''}
                      onChange={handleChange('contact_person_name')}
                      placeholder="Fornavn Efternavn"
                      helperText="Kun til intern brug"
                    />
                  </Box>

                  <Box sx={{ flex: '1 1 300px' }}>
                    <TextField
                      fullWidth
                      type="email"
                      label="Kontaktperson email"
                      value={formData.contact_person_email || ''}
                      onChange={handleChange('contact_person_email')}
                      placeholder="kontakt@firma.dk"
                      error={!!validationErrors.contact_person_email}
                      helperText={validationErrors.contact_person_email || 'Kun til intern brug'}
                    />
                  </Box>
                </Box>

                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 4 }}>
                  Sociale Medier
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  <Box sx={{ flex: '1 1 300px' }}>
                    <TextField
                      fullWidth
                      label="Facebook"
                      value={formData.facebook_link || ''}
                      onChange={handleChange('facebook_link')}
                      placeholder="https://facebook.com/jeresfirma"
                      error={!!validationErrors.facebook_link}
                      helperText={validationErrors.facebook_link || 'Link til jeres Facebook side'}
                    />
                  </Box>

                  <Box sx={{ flex: '1 1 300px' }}>
                    <TextField
                      fullWidth
                      label="Instagram"
                      value={formData.instagram || ''}
                      onChange={handleChange('instagram')}
                      placeholder="@brugernavn"
                      error={!!validationErrors.instagram}
                      helperText={validationErrors.instagram || '@brugernavn eller https://instagram.com/brugernavn'}
                    />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  <Box sx={{ flex: '1 1 300px' }}>
                    <TextField
                      fullWidth
                      label="Google Business"
                      value={formData.google_business_link || ''}
                      onChange={handleChange('google_business_link')}
                      placeholder="https://g.page/jeresfirma"
                      error={!!validationErrors.google_business_link}
                      helperText={validationErrors.google_business_link || 'Link fra Google Maps'}
                    />
                  </Box>

                  <Box sx={{ flex: '1 1 300px' }}>
                    <TextField
                      fullWidth
                      label="Trustpilot"
                      value={formData.trustpilot_url || ''}
                      onChange={handleChange('trustpilot_url')}
                      placeholder="https://dk.trustpilot.com/review/jeresfirma"
                      error={!!validationErrors.trustpilot_url}
                      helperText={validationErrors.trustpilot_url || 'Link til jeres Trustpilot profil'}
                    />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, alignItems: 'flex-start' }}>
                  <Box sx={{ flex: '1 1 300px' }}>
                    <TextField
                      fullWidth
                      label="Rating (0-5)"
                      type="number"
                      value={formData.rating || ''}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value)
                        if (!isNaN(value) && value >= 0 && value <= 5) {
                          setFormData({ ...formData, rating: value })
                        } else if (e.target.value === '') {
                          setFormData({ ...formData, rating: undefined })
                        }
                      }}
                      disabled={!isAdmin}
                      placeholder="0.0 - 5.0"
                      inputProps={{ min: 0, max: 5, step: 0.1 }}
                      helperText={isAdmin ? "Kun administratorer kan redigere rating" : "Rating kan kun redigeres af administratorer"}
                      sx={{
                        '& .MuiInputBase-input.Mui-disabled': {
                          color: 'text.secondary',
                          WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
                        },
                      }}
                    />
                  </Box>

                  <Box sx={{ flex: '1 1 300px', pt: 1 }}>
                    <Button
                      variant="outlined"
                      onClick={fetchTrustpilotRating}
                      disabled={fetchingRating || !formData.trustpilot_url}
                      startIcon={fetchingRating ? <CircularProgress size={20} /> : <RefreshIcon />}
                    >
                      {fetchingRating ? 'Henter...' : 'Hent rating fra Trustpilot'}
                    </Button>
                  </Box>
                </Box>

                {ratingMessage && (
                  <Alert
                    severity={ratingMessage.includes('✅') ? 'success' : 'error'}
                    sx={{ mb: 2 }}
                    onClose={() => setRatingMessage(null)}
                  >
                    {ratingMessage}
                  </Alert>
                )}

                <GoogleRatingInfo
                  rating={formData.google_rating}
                  reviewCount={formData.google_review_count}
                  placeId={formData.google_place_id}
                  updatedAt={formData.google_rating_updated_at}
                />

                {/* Moderation flags are admin-only in RLS */}
                {isAdmin && isEdit && <BusinessReviewsList chainId={id} />}

                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 4 }}>
                  Marketing
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    label="USP 1"
                    value={formData.usp1 || ''}
                    onChange={handleChange('usp1')}
                    placeholder="F.eks. 'Lang erfaring'"
                    inputProps={{ maxLength: 30 }}
                    helperText={`${(formData.usp1 || '').length}/30 tegn`}
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    label="USP 2"
                    value={formData.usp2 || ''}
                    onChange={handleChange('usp2')}
                    placeholder="F.eks. 'Personlig service'"
                    inputProps={{ maxLength: 30 }}
                    helperText={`${(formData.usp2 || '').length}/30 tegn`}
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    label="USP 3"
                    value={formData.usp3 || ''}
                    onChange={handleChange('usp3')}
                    placeholder="F.eks. 'Døgnservice'"
                    inputProps={{ maxLength: 30 }}
                    helperText={`${(formData.usp3 || '').length}/30 tegn`}
                  />
                </Box>

                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 4 }}>
                  Logo
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <LogoUpload
                  currentLogoUrl={formData.logo_url}
                  onLogoChange={(url) => setFormData({ ...formData, logo_url: url })}
                />

                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 4 }}>
                  Beskrivelse
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <RichTextEditor
                  value={formData.description || ''}
                  onChange={(html) => setFormData({ ...formData, description: html })}
                  placeholder="Beskriv kæden, historie, værdier..."
                  minHeight={300}
                />
              </Box>
            </TabPanel>

            {/* Tab 2: Locations */}
            <TabPanel value={activeTab} index={1}>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Lokationer i denne kæde
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Administrer hvilke lokationer der er en del af denne kæde
                    </Typography>
                  </Box>
                  {isEdit && (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => navigate(`/undertakers/new?chainId=${id}`)}
                    >
                      Opret ny lokation
                    </Button>
                  )}
                </Box>
                <Divider sx={{ mb: 3 }} />

                {locations.length === 0 ? (
                  <Alert severity="info">
                    Ingen lokationer tilknyttet endnu. {isEdit ? 'Klik på "Opret ny lokation" for at tilføje en lokation til denne kæde.' : 'Gem kæden først, og opret derefter lokationer.'}
                  </Alert>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Navn</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>By</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Adresse</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Separat Profil</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">Handlinger</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {locations.map((location) => (
                          <TableRow key={location.id} hover>
                            <TableCell sx={{ fontWeight: 500 }}>{location.name}</TableCell>
                            <TableCell>{location.city}</TableCell>
                            <TableCell>{location.address}</TableCell>
                            <TableCell>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={location.is_separate_entity || false}
                                    onChange={() => handleToggleSeparateEntity(location.id, location.is_separate_entity || false)}
                                  />
                                }
                                label={location.is_separate_entity ? 'Ja' : 'Nej'}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => navigate(`/undertakers/edit/${location.id}`)}
                                title="Rediger"
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveLocation(location.id)}
                                title="Fjern fra kæde"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                <Alert severity="info" sx={{ mt: 3 }}>
                  <strong>Hvad betyder "Separat Profil"?</strong><br />
                  • <strong>Nej (standard):</strong> Lokationen bruger kædens profildata (beskrivelse, kontakt, sociale medier)<br />
                  • <strong>Ja:</strong> Lokationen har egen profil med unik beskrivelse og kontaktdata
                </Alert>
              </Box>
            </TabPanel>

            {/* Tab 3: Service Area */}
            <TabPanel value={activeTab} index={2}>
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Kæde-dækning
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Definer serviceområde for hele kæden (overstyrer individuelle lokationer)
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Alert severity="info" sx={{ mb: 3 }}>
                  <strong>Hvordan virker kæde-dækning?</strong><br />
                  • <strong>Deaktiveret (standard):</strong> Hver lokation bruger sit eget serviceområde<br />
                  • <strong>Aktiveret:</strong> Alle lokationer i kæden bruger dette fælles serviceområde<br />
                  • Originale serviceområder bevares og kan gendannes ved deaktivering
                </Alert>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.use_chain_service_area || false}
                      onChange={(e) => setFormData({ ...formData, use_chain_service_area: e.target.checked })}
                    />
                  }
                  label="Brug kæde-dækning (overstyrer alle lokationer)"
                  sx={{ mb: 3 }}
                />

                {formData.use_chain_service_area ? (
                  <Box>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <strong>⚠️ Advarsel:</strong> Når aktiveret, vil alle {locations.length} lokationer i denne kæde bruge det serviceområde du tegner her. 
                      Deres egne serviceområder bevares, men bruges ikke til at beregne dækning.
                    </Alert>

                    {locations.length > 0 && locations[0].location ? (
                      <MapDrawing
                        address="Kæde-dækning (fælles for alle lokationer)"
                        location={locations[0].location}
                        defaultRadius={50}
                        serviceArea={serviceAreaToGeoJSON(formData.chain_service_area)}
                        onServiceAreaChange={(geoJSON) => {
                          const serviceArea = geoJSON ? geoJSONToServiceArea(geoJSON) : null
                          setFormData({ ...formData, chain_service_area: serviceArea })
                        }}
                      />
                    ) : (
                      <Alert severity="warning">
                        Tilføj mindst én lokation med koordinater for at kunne tegne kæde-dækning.
                      </Alert>
                    )}
                  </Box>
                ) : (
                  <Alert severity="info">
                    Kæde-dækning er deaktiveret. Hver lokation bruger sit eget serviceområde.
                  </Alert>
                )}

                {locations.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                      Lokationer i denne kæde ({locations.length}):
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {locations.map(loc => (
                        <Chip
                          key={loc.id}
                          label={`${loc.city}`}
                          size="small"
                          variant="outlined"
                          icon={loc.location?.lat ? <LocationOnIcon /> : <WarningIcon />}
                          color={loc.location?.lat ? 'success' : 'warning'}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </TabPanel>

            {/* Tab 4: Services */}
            <TabPanel value={activeTab} index={3}>
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Services & Priser
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Udfyld information om kædens services, priser og tilbud. Disse data deles med alle lokationer i kæden (undtagen separate enheder).
                </Typography>
                <Divider sx={{ mb: 3 }} />

                {servicesTemplate ? (
                  <ServicesFormRenderer
                    template={servicesTemplate}
                    data={servicesData}
                    onChange={handleServicesDataChange}
                  />
                ) : (
                  <Alert severity="warning">
                    Ingen services-skabelon fundet. Kontakt administrator for at oprette en.
                  </Alert>
                )}
              </Box>
            </TabPanel>

            {/* Tab 5: Prisberegner (Admin or if enabled) */}
            {(isAdmin || formData.prisberegner_enabled) && (
              <TabPanel value={activeTab} index={4}>
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Prisberegner
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Indstil priser for prisberegneren. Disse priser gælder for alle lokationer i kæden.
                  </Typography>
                  <Divider sx={{ mb: 3 }} />

                  {/* Enable toggle (admin only) */}
                  {isAdmin && (
                    <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.prisberegner_enabled || false}
                            onChange={(e) => setFormData({ ...formData, prisberegner_enabled: e.target.checked })}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              Aktivér Prisberegner
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Når aktiveret, kan kæden udfylde prisberegner-priser for alle lokationer
                            </Typography>
                          </Box>
                        }
                      />
                    </Box>
                  )}

                  {formData.prisberegner_enabled ? (
                    <PrisberegnerForm ref={prisberegnerFormRef} chainId={id} isAdmin={isAdmin} />
                  ) : (
                    <Alert severity="warning">
                      Prisberegneren er ikke aktiveret. {isAdmin ? 'Aktivér den ovenfor.' : 'Kontakt administrator for at aktivere.'}
                    </Alert>
                  )}
                </Box>
              </TabPanel>
            )}

            {/* Tab 6: Boost (Admin Only) */}
            {isAdmin && (
              <TabPanel value={activeTab} index={5}>
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Boost Indstillinger
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Få alle lokationer i denne kæde til at vises højere i søgeresultater
                  </Typography>
                  <Divider sx={{ mb: 3 }} />

                  <Alert severity="info" sx={{ mb: 3 }}>
                    <strong>⚠️ Vigtigt:</strong> Boost gælder for ALLE lokationer i denne kæde (medmindre de er markeret som separate enheder)
                  </Alert>

                  {/* Global Boost Toggle */}
                  <Box sx={{ mb: 3, p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.global_boost || false}
                          onChange={(e) => setFormData({ ...formData, global_boost: e.target.checked })}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            Aktiver Global Boost
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Viser alle lokationer i kæden højere i søgeresultater inden for boost-området
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>

                  {/* Boost Priority */}
                  <Box sx={{ mb: 4 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Boost Prioritet"
                      value={formData.boost_priority || 0}
                      onChange={(e) => setFormData({ ...formData, boost_priority: parseInt(e.target.value) || 0 })}
                      helperText="Højere tal = højere prioritet (0 = normal, 1-10 = boostet, 10+ = meget boostet)"
                      disabled={!formData.global_boost}
                      inputProps={{ min: 0, max: 100 }}
                    />
                  </Box>

                  {formData.global_boost && (
                    <>
                      <Divider sx={{ my: 3 }} />
                      
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                        Boost Område
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Vælg hvor boost skal gælde. Du kan enten bruge de eksisterende serviceområder eller tegne et tilpasset boost-område.
                      </Typography>

                      {/* Show current coverage info */}
                      <Alert severity="info" sx={{ mb: 3 }}>
                        <strong>Nuværende dækning:</strong>{' '}
                        {formData.use_chain_service_area 
                          ? 'Kæden bruger et fælles serviceområde for alle lokationer.'
                          : `Hver lokation har sit eget serviceområde (${locations.filter(l => l.service_area).length} af ${locations.length} har defineret område).`
                        }
                      </Alert>

                      <RadioGroup
                        value={hasBoostAreaEnabled((formData as any).boost_area) ? 'custom' : 'service_area'}
                        onChange={(e) => {
                          if (e.target.value === 'service_area') {
                            // Clear boost_area to use existing service areas
                            setFormData({ ...formData, boost_area: null } as any)
                          } else if (e.target.value === 'custom') {
                            // Initialize boost_area with a placeholder to enable custom drawing
                            // Set to empty object first - map will allow drawing a new area
                            setFormData({ ...formData, boost_area: {} } as any)
                          }
                        }}
                        sx={{ mb: 3 }}
                      >
                        <FormControlLabel
                          value="service_area"
                          control={<Radio />}
                          label={
                            <Box>
                              <Typography variant="body1">Brug eksisterende serviceområder (standard)</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formData.use_chain_service_area 
                                  ? 'Boost gælder i hele kædens fælles serviceområde'
                                  : 'Boost gælder i hver lokations eget serviceområde'}
                              </Typography>
                            </Box>
                          }
                        />
                        <FormControlLabel
                          value="custom"
                          control={<Radio />}
                          label={
                            <Box>
                              <Typography variant="body1">Tegn tilpasset boost-område</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Boost gælder kun i ét specifikt område du tegner
                              </Typography>
                            </Box>
                          }
                        />
                      </RadioGroup>

                      {/* Show map for custom boost area */}
                      {hasBoostAreaEnabled((formData as any).boost_area) ? (
                        <Box>
                          <Alert severity="info" sx={{ mb: 2 }}>
                            <strong>Tilpasset boost-område:</strong> Tegn det område hvor boost skal gælde.
                            Eksisterende serviceområder vises som reference i grå.
                          </Alert>
                          
                          {locations.length > 0 && locations[0].location ? (
                            <>
                              <MapDrawing
                                address="Kæde boost-område"
                                location={locations[0].location}
                                defaultRadius={50}
                                serviceArea={serviceAreaToGeoJSON((formData as any).boost_area)}
                                backgroundArea={
                                  formData.use_chain_service_area && formData.chain_service_area
                                    ? serviceAreaToGeoJSON(formData.chain_service_area)
                                    : (() => {
                                        // Combine all location service areas into a FeatureCollection for background display
                                        const locationAreas = locations
                                          .filter(l => l.service_area)
                                          .map(l => serviceAreaToGeoJSON(l.service_area))
                                          .filter(Boolean)
                                        
                                        if (locationAreas.length === 0) return undefined
                                        
                                        // Flatten all features into one FeatureCollection
                                        const allFeatures: any[] = []
                                        locationAreas.forEach((area: any) => {
                                          if (area?.type === 'FeatureCollection') {
                                            allFeatures.push(...area.features)
                                          } else if (area?.type === 'Feature') {
                                            allFeatures.push(area)
                                          }
                                        })
                                        
                                        return allFeatures.length > 0 
                                          ? { type: 'FeatureCollection', features: allFeatures }
                                          : undefined
                                      })()
                                }
                                onServiceAreaChange={(geoJSON) => {
                                  const boostArea = geoJSON ? geoJSONToServiceArea(geoJSON) : null
                                  setFormData({ ...formData, boost_area: boostArea } as any)
                                }}
                              />
                              
                              <Button
                                variant="outlined"
                                color="warning"
                                onClick={() => setFormData({ ...formData, boost_area: null } as any)}
                                sx={{ mt: 2 }}
                              >
                                Fjern tilpasset boost-område (brug serviceområder i stedet)
                              </Button>
                            </>
                          ) : (
                            <Alert severity="warning">
                              Tilføj mindst én lokation med koordinater for at kunne tegne boost-område.
                            </Alert>
                          )}
                        </Box>
                      ) : (
                        <Box>
                          <Alert severity="success" sx={{ mb: 2 }}>
                            ✓ Boost gælder i {formData.use_chain_service_area ? 'kædens fælles serviceområde' : 'hver lokations eget serviceområde'}
                          </Alert>
                          
                          {/* Preview map showing actual boost coverage */}
                          {locations.length > 0 && locations[0].location && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                <strong>Forhåndsvisning af boost-dækning:</strong> {formData.use_chain_service_area ? 'Kædens fælles område' : 'Kombineret fra alle lokationer'}
                              </Typography>
                              <MapDrawing
                                address="Boost-dækning (skrivebeskyttet)"
                                location={locations[0].location}
                                defaultRadius={50}
                                serviceArea={
                                  formData.use_chain_service_area && formData.chain_service_area
                                    ? serviceAreaToGeoJSON(formData.chain_service_area)
                                    : (() => {
                                        // Combine all location service areas for preview
                                        const locationAreas = locations
                                          .filter(l => l.service_area)
                                          .map(l => serviceAreaToGeoJSON(l.service_area))
                                          .filter(Boolean)
                                        
                                        if (locationAreas.length === 0) return undefined
                                        
                                        const allFeatures: any[] = []
                                        locationAreas.forEach((area: any) => {
                                          if (area?.type === 'FeatureCollection') {
                                            allFeatures.push(...area.features)
                                          } else if (area?.type === 'Feature') {
                                            allFeatures.push(area)
                                          }
                                        })
                                        
                                        return allFeatures.length > 0 
                                          ? { type: 'FeatureCollection', features: allFeatures }
                                          : undefined
                                      })()
                                }
                                onServiceAreaChange={() => {
                                  // Read-only - do nothing
                                  // Show alert that this is read-only
                                }}
                              />
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                💡 Tip: Vælg "Tegn tilpasset boost-område" ovenfor hvis du vil begrænse boost til et mindre område.
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      )}

                      <Alert severity="success" sx={{ mt: 3 }}>
                        ✓ Boost er aktiveret med prioritet {formData.boost_priority || 0}. 
                        Dette gælder for {locations.filter(l => !l.is_separate_entity).length} af {locations.length} lokationer.
                      </Alert>
                    </>
                  )}

                  {!formData.global_boost && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Aktivér global boost for at konfigurere boost-område.
                    </Alert>
                  )}

                  {locations.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                        Påvirkede Lokationer ({locations.filter(l => !l.is_separate_entity).length} af {locations.length}):
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
                        {locations.map(loc => (
                          <Chip
                            key={loc.id}
                            label={`${loc.name} - ${loc.city}`}
                            size="small"
                            variant={loc.is_separate_entity ? "outlined" : "filled"}
                            color={loc.is_separate_entity ? "default" : "primary"}
                          />
                        ))}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        * Lokationer med omrids er separate enheder og påvirkes IKKE af kæde boost
                      </Typography>
                    </Box>
                  )}
                </Box>
              </TabPanel>
            )}

          </CardContent>
        </Card>

        {/* Action Buttons */}
        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/undertakers')}
            disabled={saving}
          >
            Annuller
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="success"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={saving}
          >
            {saving ? 'Gemmer...' : 'Gem kæde'}
          </Button>
        </Box>
      </form>
    </Box>
  )
}

